import { FC, useState, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { DndContext, DragEndEvent, closestCenter } from '@dnd-kit/core'
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { Status } from 'types'
import { toast } from '@lib/toasts'
import {
  useUpdateTimeCardEntryOrder,
  useFetchEntriesByTimeCard,
} from '@hooks/useEntries'
import { DraggableEntry } from './DraggableEntry'
import { Button } from 'components/buttons'
import { DateTime } from 'luxon'
import { EntryWithId } from './types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faLock } from '@fortawesome/pro-solid-svg-icons'

interface TaskOrderAdjusterProps {
  date: DateTime
  readOnly: boolean
  timeCard: AdminTimeCard
}

export const TaskOrderAdjuster: FC<TaskOrderAdjusterProps> = ({
  date,
  readOnly,
  timeCard,
}) => {
  const { t } = useTranslation()
  const [entries, setEntries] = useState<EntryWithId[]>([])
  const [originalEntries, setOriginalEntries] = useState<EntryWithId[]>([])
  const updateEntryOrder = useUpdateTimeCardEntryOrder(timeCard.id)

  const { data: fetchedEntries, isLoading } = useFetchEntriesByTimeCard(
    timeCard.id,
  )

  useEffect(() => {
    if (fetchedEntries) {
      const sortedEntries: EntryWithId[] = fetchedEntries
        .reduce<EntryWithId[]>(
          (acc, entry) => (entry.id ? [...acc, entry as EntryWithId] : acc),
          [],
        )
        .sort((a, b) => {
          if (a.sortPosition == null || b.sortPosition == null) return 0
          return a.sortPosition - b.sortPosition
        })

      setEntries((entries) => (entries.length > 0 ? entries : sortedEntries))
      setOriginalEntries(sortedEntries)
    }
  }, [fetchedEntries, date])

  useEffect(() => {
    // Reset entries when timeCard changes
    setEntries([])
  }, [timeCard.id])

  const hasChanges = useMemo(() => {
    if (entries.length !== originalEntries.length) return true
    return entries.some(
      (entry, index) => entry.id !== originalEntries[index].id,
    )
  }, [entries, originalEntries])

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (!over || active.id === over.id) return

    const oldIndex = entries.findIndex(
      (entry) => entry.id === Number(active.id),
    )
    const newIndex = entries.findIndex((entry) => entry.id === Number(over.id))

    if (oldIndex !== -1 && newIndex !== -1) {
      setEntries(arrayMove(entries, oldIndex, newIndex))
    }
  }

  const handleMoveUp = (index: number) => {
    if (index === 0) return
    const newEntries = [...entries]
    ;[newEntries[index - 1], newEntries[index]] = [
      newEntries[index],
      newEntries[index - 1],
    ]
    setEntries(newEntries)
  }

  const handleMoveDown = (index: number) => {
    if (index === entries.length - 1) return
    const newEntries = [...entries]
    ;[newEntries[index], newEntries[index + 1]] = [
      newEntries[index + 1],
      newEntries[index],
    ]
    setEntries(newEntries)
  }

  const handleSave = async () => {
    const entryIds = entries.map((entry) => entry.id)

    try {
      await updateEntryOrder.mutateAsync(entryIds)

      toast({
        title: t('features.admin.taskOrderUpdated'),
        variant: 'success',
        content: t('features.admin.taskOrderUpdatedSuccessfully'),
      })

      setOriginalEntries(entries)
    } catch (error) {
      toast({
        title: t('common.error'),
        variant: 'error',
        content: t('features.admin.failedToUpdateTaskOrder'),
      })
    }
  }

  const handleCancel = () => {
    setEntries(originalEntries)
  }

  if (isLoading) {
    return (
      <div className="text-center">
        <div className="animate-pulse">
          <div className="h-4 bg-neutral-200 rounded w-3/4 mx-auto mb-4"></div>
          <div className="h-4 bg-neutral-200 rounded w-1/2 mx-auto"></div>
        </div>
      </div>
    )
  }

  if (entries.length === 0) {
    return (
      <div className="text-center text-neutral-400">
        {t(
          'features.timeLogging.clickTheSelectTasksButtonToBeginSelectingTasks',
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-neutral-600">
        {t('features.admin.taskOrderInfluencesOvertime')}
      </p>
      {timeCard.status === Status.Sent && !timeCard.correction && (
        <div className="p-3 items-center flex gap-3 text-xs text-orange-500 font-medium self-center">
          <FontAwesomeIcon icon={faLock} />
          {t('features.admin.taskOrderCanNoLongerBeUpdated')}
        </div>
      )}
      <DndContext
        onDragEnd={handleDragEnd}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
      >
        <SortableContext
          items={entries.map((entry) => entry.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-2 mb-6">
            {entries.map((entry, index) => (
              <DraggableEntry
                key={entry.id}
                entry={entry}
                position={index + 1}
                isEditable={!readOnly}
                onMoveUp={() => handleMoveUp(index)}
                onMoveDown={() => handleMoveDown(index)}
                canMoveUp={index > 0}
                canMoveDown={index < entries.length - 1}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      <div className="flex gap-2 justify-end">
        <Button
          variant="outlined"
          onClick={handleCancel}
          disabled={!hasChanges || readOnly}
        >
          {t('common.cancel')}
        </Button>
        <Button
          variant="default"
          onClick={() => void handleSave()}
          disabled={!hasChanges || readOnly || updateEntryOrder.isLoading}
          loading={updateEntryOrder.isLoading}
        >
          {t('common.save')}
        </Button>
      </div>
    </div>
  )
}
